import { FormikErrors, FormikTouched } from "formik";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldInput,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeatmapLegendConfigField from "./components/heamap-legend-config-field";
import { THeatmapConfigFormValues } from "./validation";
import { useTranslation } from "react-i18next";
import {
  useGenericStatusCodesQuery,
  useLazyGenericStatusCodesQuery,
} from "../../../api/network/adminApiServices";
import { APP_CONFIG } from "../../../constants/constants";
import { BasicEntityListingPayload } from "../../../api/types/Admin";
import { GroupBase, OptionsOrGroups } from "react-select";
import { asyncDebounce } from "../../../utils/common";
import { TGenericStatusCode } from "../../../api/types/GenericStatusCode";

export interface HeatmapAdminGeneralDetailFormProps {
  values: THeatmapConfigFormValues;
  errors: FormikErrors<THeatmapConfigFormValues>;
  touched: FormikTouched<THeatmapConfigFormValues>;
  handleChange: (event: React.ChangeEvent<any>) => void;
  formikSetValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  formikSetTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => void;
  userCanEdit: boolean;
  loadingData: boolean;
}

export const HeatmapAdminGeneralDetailForm: FC<
  HeatmapAdminGeneralDetailFormProps
> = ({
  values,
  errors,
  touched,
  handleChange,
  formikSetValue,
  handleBlur,
  formikSetTouched,
  userCanEdit,
  loadingData,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.heatmap.detailsPage",
  });

  const {
    data: dataInitialGenericStatusCodes,
    isFetching: isFetchingInitialGenericStatusCodes,
  } = useGenericStatusCodesQuery({
    page: 1,
    page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
    search: "",
  } as BasicEntityListingPayload);

  const [selectedGSCStart, setSelectedGSCStart] =
    useState<TSelectboxOption | null>(null);
  const [selectedGSCEnd, setSelectedGSCEnd] = useState<TSelectboxOption | null>(
    null
  );
  const prepopulatedGSCStart = useRef<boolean>(false);
  const {
    data: dataGenericStatusCodeStart,
    isFetching: isFetchingGenericStatusCodeStart,
  } = useGenericStatusCodesQuery(
    {
      page: 1,
      page_size: 1,
      search: values.start_event_code,
    } as BasicEntityListingPayload,
    {
      skip: !values.start_event_code || !!prepopulatedGSCStart.current,
    }
  );
  useEffect(() => {
    if (!!dataGenericStatusCodeStart && !isFetchingGenericStatusCodeStart) {
      prepopulatedGSCStart.current = true;
    }
  }, [dataGenericStatusCodeStart, isFetchingGenericStatusCodeStart]);

  const prepopulatedGSCEnd = useRef<boolean>(false);
  const {
    data: dataGenericStatusCodeEnd,
    isFetching: isFetchingGenericStatusCodeEnd,
  } = useGenericStatusCodesQuery(
    {
      page: 1,
      page_size: 1,
      search: values.end_event_code,
    } as BasicEntityListingPayload,
    {
      skip: !values.end_event_code || !!prepopulatedGSCEnd.current,
    }
  );
  useEffect(() => {
    if (!!dataGenericStatusCodeEnd && !isFetchingGenericStatusCodeEnd) {
      prepopulatedGSCEnd.current = true;
    }
  }, [dataGenericStatusCodeEnd, isFetchingGenericStatusCodeEnd]);

  const [
    genericStatusCodesQuery,
    { isFetching: isFetchingGenericStatusCodes },
  ] = useLazyGenericStatusCodesQuery();

  const loadOptionsHandlerGenericStatusCodes = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    const { data: genericStatusCodesResponse } = await genericStatusCodesQuery({
      page: additional?.page || 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      search_fields:
        APP_CONFIG.ENTITY_SEARCH_FIELDS.GENERIC_STATUS_CODE_SEARCH.join(","),
    });

    if (!genericStatusCodesResponse || !genericStatusCodesResponse?.results) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: 1,
        },
      };
    }
    const { results } = genericStatusCodesResponse as {
      results: TGenericStatusCode[];
    };
    return {
      options: !!results
        ? (results || ([] as TGenericStatusCode[])).map(
            (item: TGenericStatusCode, index: number) =>
              ({
                value: `${item.statuscode}`,
                label: optionLabel(item),
              }) as TSelectboxOption
          )
        : [],
      hasMore: genericStatusCodesResponse?.next ? true : false,
      additional: {
        page: (additional?.page as number) + 1 || 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerGenericStatusCodes = asyncDebounce(
    loadOptionsHandlerGenericStatusCodes,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const optionLabel = (
    genericStatusCode: TGenericStatusCode,
    statusName?: string
  ) => {
    const _statusName = statusName || genericStatusCode?.statusname;
    return `${genericStatusCode?.statuscode.toString()}${!!_statusName ? ` - ${_statusName}` : ""}`;
  };

  return (
    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
      <AdminFormFieldAsyncLazyDropdown
        // default 62465 - IGN.ON
        key={`start_event_code_${values?.start_event_code}_${dataGenericStatusCodeStart?.results?.[0]?.statusname}`}
        loadingData={
          loadingData ||
          isFetchingInitialGenericStatusCodes ||
          isFetchingGenericStatusCodes ||
          isFetchingGenericStatusCodeStart
        }
        label={t("start_event_code")}
        id="start_event_code"
        name="start_event_code"
        defaultOptions={
          dataInitialGenericStatusCodes?.results?.map((genericStatusCode) => ({
            value: genericStatusCode?.statuscode.toString(),
            label: optionLabel(genericStatusCode),
          })) || [] // Ensuring a fallback to an empty array if results are undefined
        }
        defaultOption={
          !!values?.start_event_code
            ? {
                value: values.start_event_code,
                label:
                  selectedGSCStart?.label ||
                  optionLabel(
                    {
                      statuscode: parseInt(values.start_event_code),
                    } as TGenericStatusCode,
                    dataGenericStatusCodeStart?.results?.[0]?.statusname ||
                      undefined
                  ),
              }
            : undefined
        }
        loadOptions={debouncedLoadOptionsHandlerGenericStatusCodes}
        onChange={(e) => {
          setSelectedGSCStart(e);
          formikSetValue("start_event_code", e?.value);
        }}
        onBlur={(e: any) => {
          formikSetTouched("start_event_code", true);
          handleBlur(e);
        }}
        touched={touched.start_event_code}
        error={errors.start_event_code}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      <AdminFormFieldAsyncLazyDropdown
        // default 62467 - IGN.OFF
        key={`end_event_code_${values?.end_event_code}_${dataGenericStatusCodeEnd?.results?.[0]?.statusname}`}
        loadingData={
          loadingData ||
          isFetchingInitialGenericStatusCodes ||
          isFetchingGenericStatusCodes ||
          isFetchingGenericStatusCodeEnd
        }
        label={t("end_event_code")}
        id="end_event_code"
        name="end_event_code"
        defaultOptions={
          dataInitialGenericStatusCodes?.results?.map((genericStatusCode) => ({
            value: genericStatusCode?.statuscode.toString(),
            label: optionLabel(genericStatusCode),
          })) || [] // Ensuring a fallback to an empty array if results are undefined
        }
        defaultOption={
          !!values?.end_event_code
            ? {
                value: values.end_event_code,
                label:
                  selectedGSCEnd?.label ||
                  optionLabel(
                    {
                      statuscode: parseInt(values.end_event_code),
                    } as TGenericStatusCode,
                    dataGenericStatusCodeEnd?.results?.[0]?.statusname ||
                      undefined
                  ),
              }
            : undefined
        }
        loadOptions={debouncedLoadOptionsHandlerGenericStatusCodes}
        onChange={(e: any) => {
          setSelectedGSCEnd(e);
          formikSetValue("end_event_code", e?.value);
        }}
        onBlur={(e: any) => {
          formikSetTouched("end_event_code", true);
          handleBlur(e);
        }}
        touched={touched.end_event_code}
        error={errors.end_event_code}
        disabled={!userCanEdit}
        detailsFormField={true}
      />

      {/* Legend */}
      <div className="col-span-12 grid grid-cols-12 gap-4">
        <label className="col-span-12 block text-sm font-display font-semibold text-field-label-valid">
          {t("legend_configuration")}
        </label>
        <div className="col-span-12 flex flex-col gap-4">
          {values.legends.map((legend, i) => (
            <HeatmapLegendConfigField
              key={i}
              value={legend}
              index={i}
              handleAddLegend={() => {
                formikSetValue("legends", [
                  ...values.legends.slice(0, i + 1),
                  {
                    color: "#00bcd4",
                    start_hour: 0,
                    end_hour: 24,
                  },
                  ...values.legends.slice(i + 1),
                ]);
                setTimeout(() => {
                  formikSetTouched("legends");
                }, 100);
              }}
              handleColorChange={(name, value) => {
                formikSetValue(name, value.hex);
                setTimeout(() => {
                  formikSetTouched(name);
                }, 100);
              }}
              handleInputFieldChange={(e) => {
                formikSetValue(e.target.name, e.target.value);
                setTimeout(() => {
                  formikSetTouched(e.target.name);
                }, 100);
              }} // {handleChange}
              handleRemoveLegend={() => {
                formikSetValue(
                  "legends",
                  values.legends.filter((_, index) => index !== i)
                );
                setTimeout(() => {
                  formikSetTouched("legends");
                }, 100);
              }}
              handleBlur={handleBlur}
              showRemoveButton={values.legends.length > 1}
              touched={touched.legends?.[i]}
              userCanEdit={userCanEdit}
              errors={errors.legends?.[i]}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
